import { localizedAccountDisplayName } from "../utils/localizedAccountDisplayName";
// src/pages/GeneralJournal.tsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  XMarkIcon,
  EllipsisVerticalIcon,
} from "@heroicons/react/24/solid";

import { apiGet } from "../api/client";
import { AppleComboBox } from "../components/AppleComboBox";
import { ReportDateRangeFilter } from "../components/ReportDateRangeFilter";
import { companyRoute } from "../utils/routeHelpers";
import {
  ymdMonthStartLocal,
  ymdTodayLocal,
} from "../utils/dateUtils";
import { formatNumber } from "../utils/formatNumber";
import { downloadCsv } from "../utils/downloadCsv";
import { A, pillClass } from "../styles/appleClasses";
import { formatMMDDYYYYFromYMD } from "../utils/dateUtils";
import { Button } from "../components/Button";
import { JOURNAL_FOCUS_SCROLL_STATE } from "../components/ScrollToTop";

type JournalRow = {
  date: string; // YYYY-MM-DD
  entry_id: number;
  entry_no: number | null;
  type: string | null;
  // Raw persisted JournalEntry.source_type from the backend read path.
  // Manual-adjustment provenance is driven ONLY by this === "adjusting"
  // (JE-PROVENANCE-1), never inferred from description/journal-no/source_id.
  source_type?: string | null;
  source_id?: number | null;

  // backend combined Source + Description into `source`
  source: string;
  source_link?: string | null;

  account_id: number;
  account: string | number;
  account_type?: string | null;
  account_kind?: string | null;
  debit: number;
  credit: number;
  voided?: boolean | null;
};

type Group = {
  entry_id: number;
  entry_no: number | null;
  date: string;
  header: JournalRow; // first line used for entry metadata (link, voided, details)
  lines: JournalRow[]; // all lines (debit first)
};

type SortId =
  | "date_desc"
  | "date_asc"
  | "account_asc"
  | "account_desc"
  | "details_asc"
  | "details_desc";

type SortOption = { id: SortId; label: string };

const SORT_OPTIONS: SortOption[] = [
  { id: "date_desc", label: "Date (newest first)" },
  { id: "date_asc", label: "Date (oldest first)" },
  { id: "account_asc", label: "Account (A → Z)" },
  { id: "account_desc", label: "Account (Z → A)" },
  { id: "details_asc", label: "Details (A → Z)" },
  { id: "details_desc", label: "Details (Z → A)" },
];

const ROWS_PER_PAGE = 50;

// Manual-adjustment provenance (JE-PROVENANCE-1). Driven exclusively by the
// persisted source_type the backend exposes -- never inferred from the
// description, journal number, or source_id.
const MANUAL_ADJUSTMENT_SOURCE_TYPE = "adjusting";

function isManualAdjustment(row: JournalRow): boolean {
  return (row.source_type ?? "") === MANUAL_ADJUSTMENT_SOURCE_TYPE;
}

// ---- Helpers ----

function fmtDateMDY(ymd: string) {
  return formatMMDDYYYYFromYMD(ymd);
}

function normalize(s: unknown) {
  return String(s ?? "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function fmtMoney(n?: number) {
  const v = Number(n || 0);
  if (!v) return "";
  const rounded = Math.round(v * 100) / 100;
  const base = formatNumber(rounded);
  if (base.includes(".")) {
    const [a, bRaw] = base.split(".");
    const b = (bRaw || "").padEnd(2, "0").slice(0, 2);
    return `${a}.${b}`;
  }
  return `${base}.00`;
}

// UI-only rename (optional)
const ownerize = (v: unknown) =>
  typeof v === "string"
    ? v
        .replace(/shareholders[’']\b/gi, "Owner’s")
        .replace(/shareholder[’']\b/gi, "Owner’s")
        .replace(/\bshareholders?\b/gi, "Owner")
    : v;

// Fix backend concatenation without losing real detail.
// - If backend sends "X — X", show "X" (dedupe)
// - If backend sends "Type — Description", show the Description
function cleanDetails(s: string) {
  const t = String(s ?? "").trim();
  const parts = t.split(/\s*(?:—|–|--)\s*/);

  if (parts.length <= 1) return t;

  const left = (parts[0] || "").trim();
  const right = parts.slice(1).join(" — ").trim();

  if (!right) return left;

  // duplicate like "Purchase X — Purchase X"
  if (normalize(left) === normalize(right)) return left;

  // otherwise prefer the richer description (right side)
  return right;
}

function resolveLink(row: JournalRow): string | null {
  // ✅ ALWAYS trust backend first
  if (row?.source_link) return row.source_link;

  const t = normalize(row.type || "");
  const s = normalize(row.source || "");
  const id = row.source_id;

  if (!id) return null;

  // fallback heuristics ONLY if backend did not provide link

  // Deposits
  if (t.includes("deposit") || s.includes("deposit")) {
    return `/deposits/${id}`;
  }

  // Prepaids
  if (
    t.includes("prepaid") ||
    s.includes("prepaid") ||
    t.includes("prepayment") ||
    s.includes("prepayment")
  ) {
    return `/prepaids/${id}`;
  }

  // Fixed Assets
  if (
    t.includes("fixed asset") ||
    s.includes("fixed asset") ||
    t.includes("fixed-asset") ||
    s.includes("fixed-asset") ||
    t.includes("asset purchase") ||
    s.includes("asset purchase")
  ) {
    return `/fixed-assets/${id}`;
  }

  return null;
}

function groupRows(rows: JournalRow[]): Group[] {
  const map = new Map<number, JournalRow[]>();

  for (const r of rows) {
    const arr = map.get(r.entry_id) || [];
    arr.push(r);
    map.set(r.entry_id, arr);
  }

  const groups: Group[] = [];

  map.forEach((lines0, entry_id) => {
    const lines = [...lines0]
      .filter((r) => (r.debit || 0) !== 0 || (r.credit || 0) !== 0)
      .sort((a, b) => {
        const aIsDebit = (a.debit || 0) > 0 ? 1 : 0;
        const bIsDebit = (b.debit || 0) > 0 ? 1 : 0;
        if (aIsDebit !== bIsDebit) return bIsDebit - aIsDebit; // debit first
        return (a.account_id || 0) - (b.account_id || 0);
      });

    if (lines.length === 0) return;

    const header = lines[0];
    groups.push({
      entry_id,
      entry_no: header.entry_no ?? null,
      date: header.date,
      header,
      lines,
    });
  });

  return groups;
}

function isSortId(v: string | null): v is SortId {
  return (
    v === "date_desc" ||
    v === "date_asc" ||
    v === "account_asc" ||
    v === "account_desc" ||
    v === "details_asc" ||
    v === "details_desc"
  );
}

// -------------------- Small menu (3 dots) --------------------
function MoreMenu({
  disabled,
  onExport,
}: {
  disabled: boolean;
  onExport: () => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "border border-gray-200 bg-white " +
    "hover:bg-gray-50 active:bg-gray-100 transition " +
    "focus:outline-none focus:ring-2 focus:ring-appleblueD " +
    "disabled:opacity-50 disabled:cursor-not-allowed";

  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        className={iconBtn}
        aria-label={t("directory.common.more")}
      >
        <EllipsisVerticalIcon className="w-5 h-5 text-gray-500" />
      </button>

      {open && !disabled && (
        <div className="absolute right-0 mt-2 w-44 bg-white border border-gray-200 rounded-xl shadow-md overflow-hidden z-[80]">
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              onExport();
            }}
            className={`w-full text-left px-3 py-2 ${A.body} hover:bg-gray-50`}
          >
            {t("directory.common.exportCsv")}
          </button>
        </div>
      )}
    </div>
  );
}

export default function GeneralJournal() {
  const { t } = useTranslation();
  // Display keys are local to this caller; stable English labels remain search data.
  const sortLabels: Record<SortId, string> = {
    date_desc: t("generalJournal.sort.dateNewest"),
    date_asc: t("generalJournal.sort.dateOldest"),
    account_asc: t("generalJournal.sort.accountAsc"),
    account_desc: t("generalJournal.sort.accountDesc"),
    details_asc: t("generalJournal.sort.detailsAsc"),
    details_desc: t("generalJournal.sort.detailsDesc"),
  };
  const navigate = useNavigate();
  const location = useLocation();
  const { company_id } = useParams<{ company_id: string }>();
  const cid = company_id ?? "";

  // AppleClasses
  const labelCls = `block ${A.label} mb-1`;
  const iconBtn =
    `${A.iconBtn} inline-flex items-center justify-center ` +
    "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-appleblueD transition";

  const headerShell =
    "sticky top-0 z-20 bg-gray-50/95 backdrop-blur supports-[backdrop-filter]:bg-gray-50/70 border-b border-gray-100";
  // ✅ locked header spacing tokens: px-2 + mb-2
  const headerInner =
    "w-full max-w-6xl mx-auto px-2 sm:px-6 pt-4 pb-2 mb-2 flex items-center";

  const searchInputCls = `${A.inputBase} pl-9 pr-3 placeholder-gray-400`;

  // Override A.card's white and source-link hover backgrounds on this page only.
  const highlightBg = "!bg-appleblue/10";
  const hoverHighlightBg = "hover:bg-appleblue/5";

  // --- Accounting semantics: color numbers only ---
  const drText = "!text-emerald-700";
  const crText = "!text-rose-700";
  const drHead = "!text-emerald-600";
  const crHead = "!text-rose-600";

  // Mobile-safe amount widths (keeps 3-column journal feel)
  const amtCol = "w-[76px] min-[390px]:w-20 min-[430px]:w-24";
  const amtCell =
    `text-right ${A.num} font-medium whitespace-nowrap ` +
    "text-[12px] min-[390px]:text-[13px]";

  // 2-line clamp without Tailwind plugin
  const clamp2: React.CSSProperties = {
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  };

  const isCreditLine = (r: JournalRow) => (r.credit || 0) > 0;

  // defaults (month-to-date)
  const defaultStart = useMemo(() => ymdMonthStartLocal(new Date()), []);
  const defaultEnd = useMemo(() => ymdTodayLocal(), []);

  // init from URL so Back preserves state
  const sp0 = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const initStart = sp0.get("start") || defaultStart;
  const initEnd = sp0.get("end") || defaultEnd;
  const initQ = sp0.get("q") || "";
  const initSortRaw = sp0.get("sort");
  const initSort: SortId = isSortId(initSortRaw) ? initSortRaw : "date_desc";
  const initPage = Math.max(1, Number(sp0.get("page") || "1") || 1);
  const initFocus = sp0.get("focus_entry_id") || "";

  const [start, setStart] = useState(initStart);
  const [end, setEnd] = useState(initEnd);

  const [rows, setRows] = useState<JournalRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState(initQ);
  const [sortId, setSortId] = useState<SortId>(initSort);
  const sortValue = useMemo(
    () => SORT_OPTIONS.find((o) => o.id === sortId) || null,
    [sortId],
  );

  const [page, setPage] = useState(initPage);

  // One pending request per mount; URL history rehydration remains separate.
  const [focusEntryIdParam, setFocusEntryIdParam] = useState(initFocus);
  const [highlightEntryId, setHighlightEntryId] = useState<number | null>(null);
  const pendingFocus = useRef<number | null>(
    Number.isSafeInteger(Number(initFocus)) && Number(initFocus) > 0 ? Number(initFocus) : null,
  );
  const focusUrlUpdate = useRef<"initial" | "page" | "expiry" | null>(
    pendingFocus.current === null ? null : "initial",
  );
  const preserveUserPage = useRef(false);
  const desktopEntries = useRef<HTMLDivElement>(null);
  const mobileEntries = useRef<HTMLDivElement>(null);
  const focusFrame = useRef<number | null>(null);
  const focusExpiry = useRef<number | null>(null);

  const clearFocusWork = useCallback(() => {
    if (focusFrame.current !== null) cancelAnimationFrame(focusFrame.current);
    if (focusExpiry.current !== null) window.clearTimeout(focusExpiry.current);
    focusFrame.current = null;
    focusExpiry.current = null;
  }, []);

  const cancelFocus = useCallback(() => {
    pendingFocus.current = null;
    focusUrlUpdate.current = null;
    preserveUserPage.current = false;
    clearFocusWork();
    setHighlightEntryId(null);
    setFocusEntryIdParam("");
  }, [clearFocusWork]);

  useEffect(() => clearFocusWork, [clearFocusWork]);

  const changePage = (next: number) => {
    const cancelledPendingLoad = loading && (pendingFocus.current !== null || preserveUserPage.current);
    cancelFocus();
    preserveUserPage.current = cancelledPendingLoad;
    setPage(next);
  };

  // sync UI state -> URL (so back preserves filters)
  useEffect(() => {
    const sp = new URLSearchParams();
    sp.set("start", start);
    sp.set("end", end);

    const q = search.trim();
    if (q) sp.set("q", q);

    if (sortId !== "date_desc") sp.set("sort", sortId);
    if (page !== 1) sp.set("page", String(page));

    if (focusEntryIdParam) sp.set("focus_entry_id", focusEntryIdParam);

    const next = `${location.pathname}?${sp.toString()}`;
    const curr = `${location.pathname}${location.search}`;
    if (next !== curr) {
      // Preserve caller state, but never inherit an earlier focus opt-out.
      const nextState = { ...location.state };
      delete nextState[JOURNAL_FOCUS_SCROLL_STATE];
      if (focusUrlUpdate.current !== null) nextState[JOURNAL_FOCUS_SCROLL_STATE] = true;
      navigate(next, { replace: true, state: nextState });
    }
    focusUrlUpdate.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end, search, sortId, page, focusEntryIdParam]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await apiGet<JournalRow[]>(
          `/general-journal?start=${start}&end=${end}`,
        );
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setError("Failed to load General Journal.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [start, end]);

  useEffect(() => {
    // The pending focus chooses its page after these rows have loaded.
    if (pendingFocus.current === null) {
      if (!preserveUserPage.current) setPage(1);
      preserveUserPage.current = false;
    }
  }, [search, rows.length, sortId]);

  const groupsAll = useMemo(() => groupRows(rows), [rows]);

  const filteredSortedGroups = useMemo(() => {
    const q = normalize(search);

    // Filter
    let gs = groupsAll;
    if (q) {
      gs = gs.filter((g) => {
        const header = g.header;
        const hay = normalize(
          [
            g.date,
            g.entry_no ?? g.entry_id,
            header.source,
            header.type ?? "",
            header.source_id ?? "",
          ].join(" "),
        );
        if (hay.includes(q)) return true;

        return g.lines.some((r) =>
          normalize(
            [
              r.account,
              fmtMoney(r.debit),
              fmtMoney(r.credit),
              r.source,
              r.type ?? "",
              r.source_id ?? "",
            ].join(" "),
          ).includes(q),
        );
      });
    }

    // Sort
    const sorted = [...gs].sort((a, b) => {
      const aDate = a.date;
      const bDate = b.date;
      const aNo = a.entry_no ?? a.entry_id;
      const bNo = b.entry_no ?? b.entry_id;

      const aDetails = normalize(cleanDetails(a.header.source || ""));
      const bDetails = normalize(cleanDetails(b.header.source || ""));

      const aAccount = normalize(a.lines[0]?.account);
      const bAccount = normalize(b.lines[0]?.account);

      const byDateDesc = () => {
        if (aDate !== bDate) return aDate > bDate ? -1 : 1;
        return bNo - aNo;
      };
      const byDateAsc = () => {
        if (aDate !== bDate) return aDate < bDate ? -1 : 1;
        return aNo - bNo;
      };

      if (sortId === "date_desc") return byDateDesc();
      if (sortId === "date_asc") return byDateAsc();

      if (sortId === "account_asc" || sortId === "account_desc") {
        const cmp = aAccount.localeCompare(bAccount);
        if (cmp !== 0) return sortId === "account_asc" ? cmp : -cmp;
        return byDateDesc();
      }

      // details
      const cmp = aDetails.localeCompare(bDetails);
      if (cmp !== 0) return sortId === "details_asc" ? cmp : -cmp;
      return byDateDesc();
    });

    return sorted;
  }, [groupsAll, search, sortId]);

  const totalFiltered = filteredSortedGroups.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / ROWS_PER_PAGE));
  const pagedGroups = filteredSortedGroups.slice(
    (page - 1) * ROWS_PER_PAGE,
    page * ROWS_PER_PAGE,
  );

  useEffect(() => {
    const entryId = pendingFocus.current;
    if (loading || entryId === null) return;
    const index = filteredSortedGroups.findIndex((g) => g.entry_id === entryId);
    if (error || index < 0) {
      cancelFocus();
      setPage(1);
      return;
    }
    const targetPage = Math.floor(index / ROWS_PER_PAGE) + 1;
    if (page !== targetPage) {
      focusUrlUpdate.current = "page";
      setPage(targetPage); // Automatic selection must not cancel its own focus.
      return;
    }

    focusFrame.current = requestAnimationFrame(() => {
      focusFrame.current = null;
      if (pendingFocus.current !== entryId) return;
      // Layout rects exclude display:none ancestors; computed visibility also
      // excludes visibility:hidden. Off-screen cards still qualify for scrolling.
      const target = [desktopEntries.current, mobileEntries.current]
        .map((root) => root?.querySelector<HTMLElement>(`#je-${entryId}`))
        .find((el) => el && el.getClientRects().length > 0 &&
          window.getComputedStyle(el).visibility === "visible");
      if (!target) {
        cancelFocus();
        return;
      }
      pendingFocus.current = null;
      setHighlightEntryId(entryId);
      target.scrollIntoView({ behavior: "smooth", block: "center" });
      focusExpiry.current = window.setTimeout(() => {
        cancelFocus();
        focusUrlUpdate.current = "expiry";
      }, 3000);
    });
    return () => {
      if (focusFrame.current !== null) cancelAnimationFrame(focusFrame.current);
      focusFrame.current = null;
    };
  }, [loading, error, filteredSortedGroups, page, cancelFocus]);

  const exportCSV = () => {
    const out: (string | number)[][] = [
      ["Date", "Journal", "Details", "Account", "Debit", "Credit", "Voided"],
    ];

    for (const g of filteredSortedGroups) {
      const header = g.header;
      const voided = header.voided ? "Voided" : "";
      const details = cleanDetails(header.source || "");

      g.lines.forEach((r, idx) => {
        out.push([
          idx === 0 ? fmtDateMDY(g.date) : "",
          idx === 0 ? String(g.entry_no ?? g.entry_id) : "",
          idx === 0 ? details : "",
          typeof r.account === "string"
            ? (ownerize(r.account) as string)
            : String(r.account),
          r.debit ? fmtMoney(r.debit) : "",
          r.credit ? fmtMoney(r.credit) : "",
          idx === 0 ? voided : "",
        ]);
      });
    }

    const csv = out
      .map((row) =>
        row
          .map((cell) => `"${String(cell ?? "").replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\n");

    downloadCsv(`GeneralJournal_${start}_to_${end}.csv`, csv);
  };

  const dateSortActive =
    sortId === "date_asc" ? "asc" : sortId === "date_desc" ? "desc" : null;

  const onDateHeaderClick = () => {
    setSortId((prev) => (prev === "date_asc" ? "date_desc" : "date_asc"));
  };

  const accountSortActive =
    sortId === "account_asc"
      ? "asc"
      : sortId === "account_desc"
        ? "desc"
        : null;

  const onAccountHeaderClick = () => {
    setSortId((prev) =>
      prev === "account_asc" ? "account_desc" : "account_asc",
    );
  };

  const voidPillCls = `${A.pillBase} bg-red-50 text-red-700 ring-red-100`;
  // Restrained amber pill (legitimate feature marker, NOT an error state).
  const manualAdjustmentPillCls = pillClass("warning");

  const pagerBtnExtraCls = "h-9 px-3 rounded-xl";

  return (
    <div className={`min-h-screen bg-gray-50 ${A.body}`}>
      {/* Header (Apple standard) */}
      <div className={headerShell}>
        <div className={headerInner}>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={iconBtn}
            aria-label={t("directory.common.back")}
          >
            <ArrowLeftIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
          </button>

          <div className="flex-1 flex justify-center pointer-events-none">
            <h2 className={`${A.title} text-center`}>{t("generalJournal.title")}</h2>
          </div>

          <button
            type="button"
            onClick={() => navigate(companyRoute(cid, "/"))}
            className={iconBtn}
            aria-label={t("directory.common.close")}
          >
            <XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600 transition" />
          </button>
        </div>
      </div>

      <div className="w-full max-w-6xl mx-auto px-2 sm:px-6 mb-3">
        {/* DateRange + ⋮ (keep on same row on mobile) */}
        <div className="grid grid-cols-[1fr_1fr_auto] items-end gap-3 mb-3">
          <div className="col-span-2">
            <ReportDateRangeFilter
              companyId={company_id}
              fromLabel={t("sales.shared.from")}
              toLabel={t("sales.shared.to")}
              openCalendarLabel={t("sales.shared.openCalendar")}
              startDate={start}
              endDate={end}
              onChange={({ startDate, endDate }) => {
                cancelFocus();
                setStart(startDate);
                setEnd(endDate);
              }}
            />
            {loading && <div className={`${A.label} mt-1`}>{t("generalJournal.updating")}</div>}
          </div>

          <div className="pb-[2px] justify-self-end">
            <MoreMenu
              disabled={filteredSortedGroups.length === 0}
              onExport={exportCSV}
            />
          </div>
        </div>

        {/* Search + Sort */}
        <div className="flex flex-col md:flex-row gap-3 items-start md:items-end">
          <div className="w-full md:flex-1 min-w-[220px]">
            <label className={labelCls}>{t("dashboard.search")}</label>
            <div className="relative">
              <span className="absolute left-3 top-2.5 text-gray-400 pointer-events-none">
                <svg
                  width="18"
                  height="18"
                  fill="none"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle
                    cx="11"
                    cy="11"
                    r="7"
                    stroke="currentColor"
                    strokeWidth="2"
                  />
                  <path
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    d="M20 20l-3-3"
                  />
                </svg>
              </span>
              <input
                type="text"
                placeholder={t("dashboard.search")}
                value={search}
                onChange={(e) => { cancelFocus(); setSearch(e.target.value); }}
                className={searchInputCls}
              />
            </div>
          </div>

          <div className="w-full md:w-80 relative z-[60]">
            <label className={labelCls}>{t("directory.common.sort")}</label>
            <AppleComboBox<SortOption>
              options={SORT_OPTIONS}
              value={sortValue}
              onChange={(opt) => { cancelFocus(); setSortId((opt?.id ?? "date_desc") as SortId); }}
              getOptionLabel={(opt) => (opt ? sortLabels[opt.id] : "")}
              getOptionSearchText={(option) => option.label}
              preserveQueryOnDisplayChange
              placeholder={t("generalJournal.sort.placeholder")}
              emptyText={t("sales.shared.noResults")}
              toggleOptionsLabel={t("sales.shared.toggleOptions")}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`${A.card} w-full max-w-6xl mx-auto px-2 md:px-6 py-5`}>
        {error && <div className="text-red-600 text-[13px] mb-3">{t("generalJournal.errors.load")}</div>}

        {filteredSortedGroups.length === 0 && !loading ? (
          <div className="text-center py-10 text-gray-400 text-[13px]">
            {t("generalJournal.empty")}
          </div>
        ) : (
          <>
            {/* Desktop cards (Apple-style): one card per journal entry */}
            <div ref={desktopEntries} className="hidden md:block max-h-[70vh] overflow-y-auto pr-1">
              <div className="flex flex-col gap-3">
                {pagedGroups.map((g) => {
                  const header = g.header;
                  const link = resolveLink(header);
                  const clickable = !!link;
                  const isHighlight = highlightEntryId === g.entry_id;

                  return (
                    <div
                      id={`je-${g.entry_id}`}
                      key={g.entry_id}
                      className={[
                        `${A.card} p-4 transition`,
                        isHighlight ? highlightBg : "",
                        clickable ? `${hoverHighlightBg} cursor-pointer` : "",
                      ].join(" ")}
                      onClick={() => {
                        if (link) navigate(companyRoute(cid, link));
                      }}
                      title={clickable ? t("generalJournal.openSource") : undefined}
                    >
                      {/* Header */}
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div className="flex items-center gap-4 min-w-0">
                          <div className="text-[13px] font-semibold text-gray-700 whitespace-nowrap">
                            {fmtDateMDY(g.date)}
                          </div>
                          <div className={`${A.label} whitespace-nowrap`}>
                            {t("generalJournal.journalNumber", { number: g.entry_no ?? g.entry_id })}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          {isManualAdjustment(header) ? (
                            <span className={manualAdjustmentPillCls}>
                              {t("generalJournal.manualAdjustment")}
                            </span>
                          ) : null}
                          {header.voided ? (
                            <span className={voidPillCls}>{t("purchases.status.voided")}</span>
                          ) : null}
                        </div>
                      </div>

                      {/* Mini journal table */}
                      <div className="rounded-2xl bg-white/80 ring-1 ring-black/5 overflow-hidden">
                        <div className="grid grid-cols-[1fr_130px_130px] items-center px-4 py-2 bg-gray-50/70 border-b border-gray-100 text-xs font-medium text-gray-500">
                          <div>{t("generalJournal.account")}</div>
                          <div className={`text-right ${drHead}`}>{t("generalJournal.debit")}</div>
                          <div className={`text-right ${crHead}`}>{t("generalJournal.credit")}</div>
                        </div>

                        <div className="divide-y divide-gray-100">
                          {g.lines.map((r, idx) => {
                            const fallback = typeof r.account === "string" ? String(ownerize(r.account)) : String(r.account);
                            const acct = localizedAccountDisplayName({ name: typeof r.account === "string" ? r.account : null, type: r.account_type, kind: r.account_kind }, t, fallback);

                            const cr = isCreditLine(r);

                            return (
                              <div
                                key={`${g.entry_id}-${idx}-${r.account_id}`}
                                className="grid grid-cols-[1fr_130px_130px] items-center px-4 py-3"
                              >
                                <div className={`min-w-0 pr-3 ${cr ? "pl-8" : ""}`}>
                                  <div
                                    className={`${A.body} leading-snug break-words`}
                                    style={clamp2}
                                    title={acct}
                                  >
                                    {acct}
                                  </div>
                                </div>

                                <div
                                  className={`text-right ${A.num} font-medium ${
                                    r.debit ? drText : ""
                                  }`}
                                >
                                  {fmtMoney(r.debit)}
                                </div>

                                <div
                                  className={`text-right ${A.num} font-medium ${
                                    r.credit ? crText : ""
                                  }`}
                                >
                                  {fmtMoney(r.credit)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Details footer */}
                      <div className="mt-3 rounded-xl bg-gray-50 px-4 py-3 flex flex-col gap-1 min-h-[48px] justify-center">
                        <div className={`${A.label} leading-snug break-words`}>
                          {cleanDetails(header.source || "")}
                        </div>
                        {isManualAdjustment(header) ? (
                          <div className="text-xs text-amber-700 leading-snug">
                            {t("generalJournal.manualExplanation")}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Mobile cards: header + mini journal table + details footer */}
            <div ref={mobileEntries} className="block md:hidden">
              <div className="flex flex-col gap-3">
                {pagedGroups.map((g) => {
                  const header = g.header;
                  const link = resolveLink(header);
                  const clickable = !!link;
                  const isHighlight = highlightEntryId === g.entry_id;

                  return (
                    <div
                      id={`je-${g.entry_id}`}
                      key={g.entry_id}
                      className={[
                        `${A.card} p-4 transition`,
                        isHighlight ? highlightBg : "",
                        clickable ? `${hoverHighlightBg} cursor-pointer` : "",
                      ].join(" ")}
                      onClick={() => {
                        if (link) navigate(companyRoute(cid, link));
                      }}
                      title={clickable ? t("generalJournal.openSource") : undefined}
                    >
                      <div className="flex justify-between items-start mb-1 gap-3">
                        <div className="min-w-0">
                          <div className="text-[13px] font-semibold text-gray-700">
                            {fmtDateMDY(g.date)}
                          </div>
                          <div className={`${A.label} mt-0.5`}>
                            {t("generalJournal.journalNumber", { number: g.entry_no ?? g.entry_id })}
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-1 shrink-0">
                          {isManualAdjustment(header) ? (
                            <span className={manualAdjustmentPillCls}>
                              {t("generalJournal.manualAdjustment")}
                            </span>
                          ) : null}
                          {header.voided ? (
                            <span className={voidPillCls}>{t("purchases.status.voided")}</span>
                          ) : null}
                        </div>
                      </div>

                      {/* Mobile: mini journal table (Account | Dr | Cr), Cr indented */}
                      <div className="mt-3 rounded-xl bg-white/70 ring-1 ring-black/5 overflow-hidden">
                        <div className="flex items-center px-3 py-2 bg-gray-50/50 border-b border-gray-100 text-xs font-medium text-gray-500">
                          <div className="flex-1 min-w-0">{t("generalJournal.account")}</div>
                          <div className={`${amtCol} text-right ${drHead}`}>
                            {t("generalJournal.debit")}
                          </div>
                          <div className={`${amtCol} text-right ${crHead}`}>
                            {t("generalJournal.credit")}
                          </div>
                        </div>

                        <div className="divide-y divide-gray-100">
                          {g.lines.map((r, idx) => {
                            const fallback = typeof r.account === "string" ? String(ownerize(r.account)) : String(r.account);
                            const acct = localizedAccountDisplayName({ name: typeof r.account === "string" ? r.account : null, type: r.account_type, kind: r.account_kind }, t, fallback);

                            const cr = isCreditLine(r);

                            return (
                              <div
                                key={idx}
                                className="flex items-start px-3 py-2"
                              >
                                <div
                                  className={`flex-1 min-w-0 pr-2 ${
                                    cr ? "pl-6" : ""
                                  }`}
                                >
                                  <div
                                    className={`${A.body} leading-snug break-words`}
                                    style={clamp2}
                                    title={acct}
                                  >
                                    {acct}
                                  </div>
                                </div>

                                <div
                                  className={`${amtCol} ${amtCell} ${
                                    r.debit ? drText : ""
                                  }`}
                                >
                                  {fmtMoney(r.debit)}
                                </div>
                                <div
                                  className={`${amtCol} ${amtCell} ${
                                    r.credit ? crText : ""
                                  }`}
                                >
                                  {fmtMoney(r.credit)}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Details footer ONCE per entry */}
                      <div className="mt-3 flex flex-col gap-1">
                        <div className={`${A.label} leading-snug break-words`}>
                          {cleanDetails(header.source || "")}
                        </div>
                        {isManualAdjustment(header) ? (
                          <div className="text-xs text-amber-700 leading-snug">
                            {t("generalJournal.manualExplanation")}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pagination — standardized (matches other lists) */}
            <div className="flex justify-between items-center mt-4 px-1">
              <span className={`${A.label} text-appleblue select-none`}>
                {t("directory.common.showing", {
                  from: totalFiltered === 0 ? 0 : (page - 1) * ROWS_PER_PAGE + 1,
                  to: totalFiltered === 0
                    ? 0
                    : Math.min(page * ROWS_PER_PAGE, totalFiltered),
                  total: totalFiltered,
                })}
              </span>

              <div className="flex gap-2">
                <Button
                  variant="tertiary"
                  minWidth="0px"
                  className={pagerBtnExtraCls}
                  onClick={() => changePage(Math.max(1, page - 1))}
                  disabled={page <= 1}
                  type="button"
                >
                  {t("directory.common.prev")}
                </Button>

                <Button
                  variant="tertiary"
                  minWidth="0px"
                  className={pagerBtnExtraCls}
                  onClick={() => changePage(Math.min(totalPages, page + 1))}
                  disabled={page >= totalPages}
                  type="button"
                >
                  {t("directory.common.next")}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
